<template>
    <div class="p-0 md:p-6 -mt-6">
        <!-- Filter Controls -->
        <div class="mb-4 md:mb-6 bg-gray-50 rounded-lg p-3 md:p-4 border border-gray-200">
            <div class="flex items-center justify-between mb-3">
                <h3 class="m-0 p-0 text-left text-sm font-semibold text-gray-700">Filters</h3>
                <div class="text-sm font-medium text-gray-600">
                    {{ filteredAlarmsCount }} {{ filteredAlarmsCount === 1 ? 'alarm' : 'alarms' }}
                </div>
            </div>
            <div class="grid grid-cols-2 gap-4">
                <div>
                    <label class="block text-xs font-medium text-gray-600 mb-1">
                        Status
                    </label>
                    <select2 :options="statusFilterOptions"
                             v-model:value="statusFilter"
                             :allow-clear="false"
                             :close-on-select="true"
                             :disable-search="true"
                             placeholder="Select status">
                    </select2>
                </div>
                <div>
                    <label class="block text-xs font-medium text-gray-600 mb-1">
                        Alarm Type
                    </label>
                    <div class="overflow-x-auto">
                        <select2 :options="typeFilterOptions"
                                 v-model:value="typeFilter"
                                 :allow-clear="true"
                                 :close-on-select="false"
                                 :multiple="true"
                                 :disable-search="true"
                                 placeholder="All types">
                        </select2>
                    </div>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex items-center justify-center py-16">
            <div class="flex animate-pulse flex-col items-center justify-center space-y-0.5">
                <span class="lnr icon-hourglass !text-4xl !text-blue-500 mb-2 inline-block w-8 text-center"></span>
                <span class="text-base font-medium">Loading alarms</span>
                <span class="text-sm text-gray-500">Just a moment…</span>
            </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="filteredAlarms.length === 0" class="flex items-center justify-center py-16">
            <div class="flex flex-col items-center justify-center space-y-0.5">
                <span class="lnr icon-inbox !text-4xl !text-gray-400 mb-2 inline-block w-8 text-center"></span>
                <span class="text-base font-medium text-gray-700">No alarms found</span>
                <span class="text-sm text-gray-500">This point has no alarms matching your filters</span>
            </div>
        </div>

        <!-- Alarms Table -->
        <template v-else>
            <!-- Truncation Notice -->
            <div v-if="truncated" class="mb-4 flex items-center gap-2 rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2 text-sm text-yellow-800">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="flex-shrink-0"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 9v4" /><path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" /><path d="M12 16h.01" /></svg>
                Results may be incomplete. Some older alarms for this point may not be displayed.
            </div>

        <div :class="{ 'pb-6': totalPages <= 1 }">
            <div class="overflow-x-auto">
                <!-- Desktop Table -->
                <table class="hidden md:table min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                        <tr>
                            <th scope="col"
                                @click="sortTable('status')"
                                :class="[
                                    'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none focus:outline-none',
                                    sortBy === 'status' ? 'text-gray-700 bg-gray-100' : 'text-gray-500 hover:bg-gray-100'
                                ]">
                                <div class="flex items-center gap-1">
                                    Status
                                    <svg v-if="sortBy === 'status'" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                        <path v-if="sortDesc" d="M12 5v14M19 12l-7 7-7-7"/>
                                        <path v-else d="M12 19V5M5 12l7-7 7 7"/>
                                    </svg>
                                </div>
                            </th>
                            <th scope="col"
                                @click="sortTable('type')"
                                :class="[
                                    'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none focus:outline-none',
                                    sortBy === 'type' ? 'text-gray-700 bg-gray-100' : 'text-gray-500 hover:bg-gray-100'
                                ]">
                                <div class="flex items-center gap-1">
                                    Type
                                    <svg v-if="sortBy === 'type'" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                        <path v-if="sortDesc" d="M12 5v14M19 12l-7 7-7-7"/>
                                        <path v-else d="M12 19V5M5 12l7-7 7 7"/>
                                    </svg>
                                </div>
                            </th>
                            <th scope="col"
                                @click="sortTable('time')"
                                :class="[
                                    'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none focus:outline-none',
                                    sortBy === 'time' ? 'text-gray-700 bg-gray-100' : 'text-gray-500 hover:bg-gray-100'
                                ]">
                                <div class="flex items-center gap-1">
                                    Triggered
                                    <svg v-if="sortBy === 'time'" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                        <path v-if="sortDesc" d="M12 5v14M19 12l-7 7-7-7"/>
                                        <path v-else d="M12 19V5M5 12l7-7 7 7"/>
                                    </svg>
                                </div>
                            </th>
                            <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Reading
                            </th>
                            <th scope="col"
                                @click="sortTable('comments')"
                                :class="[
                                    'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none focus:outline-none',
                                    sortBy === 'comments' ? 'text-gray-700 bg-gray-100' : 'text-gray-500 hover:bg-gray-100'
                                ]">
                                <div class="flex items-center gap-1">
                                    Comments
                                    <svg v-if="sortBy === 'comments'" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                        <path v-if="sortDesc" d="M12 5v14M19 12l-7 7-7-7"/>
                                        <path v-else d="M12 19V5M5 12l7-7 7 7"/>
                                    </svg>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                        <tr v-for="alarm in paginatedAlarms"
                            :key="alarm.id"
                            @click="navigateToAlarm(alarm.id)"
                            class="hover:bg-gray-50 cursor-pointer transition-colors">
                            <td class="px-4 py-4 whitespace-nowrap">
                                <alarm-status-badge :status="alarm.status" />
                            </td>
                            <td class="px-4 py-4 whitespace-nowrap">
                                <alarm-type-badge :type="alarm.type" />
                            </td>
                            <td class="px-4 py-4">
                                <div class="text-sm text-gray-900">{{ alarm.time }}</div>
                                <div class="text-xs text-gray-500">{{ alarm.time_diff }}</div>
                            </td>
                            <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                {{ alarm.reading }}
                            </td>
                            <td class="px-4 py-4">
                                <div class="flex items-start gap-2">
                                    <span class="text-sm font-medium text-gray-900 flex-shrink-0">
                                        {{ commentCount(alarm) }}
                                    </span>
                                    <div v-if="alarm.last_comment" class="text-xs text-gray-500 min-w-0 flex-1">
                                        <div class="line-clamp-2">
                                            <span class="font-medium">{{ alarm.last_comment.user_name }}:</span>
                                            {{ alarm.last_comment.comment }}
                                        </div>
                                    </div>
                                </div>
                                <div v-if="alarm.last_comment_time" class="text-xs text-gray-400 mt-1">
                                    {{ alarm.last_comment_time }}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- Mobile Table -->
                <table class="md:hidden min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                        <tr>
                            <th scope="col" class="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider">
                                <div class="flex flex-col gap-1">
                                    <div @click="sortTable('status')"
                                         :class="[
                                             'cursor-pointer select-none flex items-center gap-1',
                                             sortBy === 'status' ? 'text-gray-700' : 'text-gray-500 hover:text-gray-700'
                                         ]">
                                        Status
                                        <svg v-if="sortBy === 'status'" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                            <path v-if="sortDesc" d="M12 5v14M19 12l-7 7-7-7"/>
                                            <path v-else d="M12 19V5M5 12l7-7 7 7"/>
                                        </svg>
                                    </div>
                                    <div @click="sortTable('type')"
                                         :class="[
                                             'cursor-pointer select-none flex items-center gap-1',
                                             sortBy === 'type' ? 'text-gray-700' : 'text-gray-500 hover:text-gray-700'
                                         ]">
                                        Type
                                        <svg v-if="sortBy === 'type'" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                            <path v-if="sortDesc" d="M12 5v14M19 12l-7 7-7-7"/>
                                            <path v-else d="M12 19V5M5 12l7-7 7 7"/>
                                        </svg>
                                    </div>
                                </div>
                            </th>
                            <th scope="col"
                                @click="sortTable('time')"
                                :class="[
                                    'px-3 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none focus:outline-none',
                                    sortBy === 'time' ? 'text-gray-700 bg-gray-100' : 'text-gray-500 hover:bg-gray-100'
                                ]">
                                <div class="flex items-center gap-1">
                                    Triggered
                                    <svg v-if="sortBy === 'time'" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <path v-if="sortDesc" d="M12 5v14M19 12l-7 7-7-7"/>
                                        <path v-else d="M12 19V5M5 12l7-7 7 7"/>
                                    </svg>
                                </div>
                            </th>
                            <th scope="col"
                                @click="sortTable('comments')"
                                :class="[
                                    'px-3 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none focus:outline-none',
                                    sortBy === 'comments' ? 'text-gray-700 bg-gray-100' : 'text-gray-500 hover:bg-gray-100'
                                ]">
                                <div class="flex items-center gap-1">
                                    Comments
                                    <svg v-if="sortBy === 'comments'" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <path v-if="sortDesc" d="M12 5v14M19 12l-7 7-7-7"/>
                                        <path v-else d="M12 19V5M5 12l7-7 7 7"/>
                                    </svg>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                        <tr v-for="alarm in paginatedAlarms"
                            :key="alarm.id"
                            @click="navigateToAlarm(alarm.id)"
                            class="hover:bg-gray-50 cursor-pointer transition-colors">
                            <!-- Combined Status & Type Column -->
                            <td class="px-3 py-3">
                                <div class="flex flex-col gap-2">
                                    <alarm-status-badge :status="alarm.status" />
                                    <alarm-type-badge :type="alarm.type" />
                                </div>
                            </td>
                            <!-- Combined Triggered & Reading Column -->
                            <td class="px-3 py-3">
                                <div class="text-xs text-gray-900">{{ alarm.time }}</div>
                                <div class="text-xs text-gray-500">{{ alarm.time_diff }}</div>
                                <div class="text-xs font-medium text-gray-900 mt-1">
                                    {{ alarm.reading }}
                                </div>
                            </td>
                            <!-- Comments Column -->
                            <td class="px-3 py-3">
                                <div class="text-xs font-medium text-gray-900">
                                    {{ commentCount(alarm) }}
                                </div>
                                <div v-if="alarm.last_comment" class="text-xs text-gray-500 mt-1">
                                    <div class="line-clamp-2">
                                        <span class="font-medium">{{ alarm.last_comment.user_name }}:</span>
                                        {{ alarm.last_comment.comment }}
                                    </div>
                                </div>
                                <div v-if="alarm.last_comment_time" class="text-xs text-gray-400 mt-1">
                                    {{ alarm.last_comment_time }}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination Controls -->
            <div v-if="totalPages > 1" class="border-t border-gray-200 bg-gray-50 px-3 md:px-6 py-4">
                <div class="flex flex-col md:flex-row items-center justify-between gap-3 md:gap-0">
                    <div class="text-xs md:text-sm text-gray-700 order-2 md:order-1">
                        Showing <span class="font-medium">{{ startItem }}</span> to <span class="font-medium">{{ endItem }}</span> of <span class="font-medium">{{ filteredAlarmsCount }}</span> results
                    </div>

                    <div class="flex gap-2 order-1 md:order-2 flex-wrap justify-center">
                        <button
                            @click="previousPage"
                            :disabled="currentPage === 1"
                            :class="[
                                'inline-flex items-center justify-center gap-1 rounded-md border px-4 py-3 md:py-2 text-sm font-medium transition-colors min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0',
                                currentPage === 1 ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            ]"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M21 5v14l-8 -7l8 -7" /><path d="M10 5v14l-8 -7l8 -7" /></svg>
                            <span class="hidden md:inline">Previous</span>
                        </button>

                        <button
                            v-for="page in visiblePages"
                            :key="page"
                            @click="goToPage(page)"
                            :disabled="page === '...'"
                            :class="[
                                'inline-flex items-center justify-center rounded-md border px-4 py-3 md:py-2 text-sm font-medium transition-colors min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0',
                                page === currentPage ? 'border-blue-500 bg-blue-50 text-blue-600' : page === '...' ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            ]"
                        >
                            {{ page }}
                        </button>

                        <button
                            @click="nextPage"
                            :disabled="currentPage === totalPages"
                            :class="[
                                'inline-flex items-center justify-center gap-1 rounded-md border px-4 py-3 md:py-2 text-sm font-medium transition-colors min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0',
                                currentPage === totalPages ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            ]"
                        >
                            <span class="hidden md:inline">Next</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M3 5v14l8 -7l-8 -7" /><path d="M14 5v14l8 -7l-8 -7" /></svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        </template>
    </div>
</template>

<script>
import axios from 'axios';
import {SonicuCoreMixin} from '@/mixins/SonicuCoreMixin';
import AlarmStatusBadge from '@/components/AlarmStatusBadge.vue';
import AlarmTypeBadge from '@/components/AlarmTypeBadge.vue';
import Select2 from '@/components/elements/select2.vue';

export default {
    name: 'MeterAlarmsSlideOver',
    mixins: [SonicuCoreMixin],
    components: {
        AlarmStatusBadge,
        AlarmTypeBadge,
        Select2
    },
    props: {
        meterId: {
            type: Number,
            required: true
        },
        meterName: {
            type: String,
            required: true
        },
        meterSerial: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            alarms: [],
            truncated: false,
            loading: false,
            statusFilter: 'all',
            typeFilter: [],
            currentPage: 1,
            perPage: 10,
            sortBy: null,
            sortDesc: true,
            statusFilterOptions: {
                'all': 'All Alarms',
                'active': 'Active Only',
                'snoozed': 'Snoozed Only',
                'resolved': 'Resolved Only'
            },
            typeFilterOptions: {
                'Threshold': 'Threshold',
                'Offline': 'Offline',
                'Validation': 'Validation'
            }
        };
    },
    computed: {
        filteredAlarms() {
            let filtered = this.alarms;

            // Filter by status
            if (this.statusFilter !== 'all') {
                filtered = filtered.filter(alarm => alarm.status === this.statusFilter);
            }

            // Filter by type
            if (Array.isArray(this.typeFilter) && this.typeFilter.length > 0) {
                filtered = filtered.filter(alarm => this.typeFilter.includes(alarm.type));
            }

            // Sort
            if (this.sortBy) {
                filtered = [...filtered].sort((a, b) => {
                    let aVal, bVal;

                    switch (this.sortBy) {
                        case 'status':
                            aVal = a.status;
                            bVal = b.status;
                            break;
                        case 'type':
                            aVal = a.type;
                            bVal = b.type;
                            break;
                        case 'time':
                            aVal = new Date(a.time_iso8601).getTime();
                            bVal = new Date(b.time_iso8601).getTime();
                            break;
                        case 'comments':
                            aVal = a.last_comment?.created_at ? new Date(a.last_comment.created_at).getTime() : 0;
                            bVal = b.last_comment?.created_at ? new Date(b.last_comment.created_at).getTime() : 0;
                            break;
                        default:
                            return 0;
                    }

                    if (aVal < bVal) return this.sortDesc ? 1 : -1;
                    if (aVal > bVal) return this.sortDesc ? -1 : 1;
                    return 0;
                });
            }

            return filtered;
        },
        filteredAlarmsCount() {
            return this.filteredAlarms.length;
        },
        totalPages() {
            return Math.ceil(this.filteredAlarmsCount / this.perPage);
        },
        paginatedAlarms() {
            const start = (this.currentPage - 1) * this.perPage;
            const end = start + this.perPage;
            return this.filteredAlarms.slice(start, end);
        },
        startItem() {
            if (this.filteredAlarmsCount === 0) {
                return 0;
            }
            return ((this.currentPage - 1) * this.perPage) + 1;
        },
        endItem() {
            const end = this.currentPage * this.perPage;
            return Math.min(end, this.filteredAlarmsCount);
        },
        visiblePages() {
            const pages = [];
            const maxVisible = 7;

            if (this.totalPages <= maxVisible) {
                for (let i = 1; i <= this.totalPages; i++) {
                    pages.push(i);
                }
            } else {
                if (this.currentPage <= 4) {
                    for (let i = 1; i <= 5; i++) {
                        pages.push(i);
                    }
                    pages.push('...');
                    pages.push(this.totalPages);
                } else if (this.currentPage >= this.totalPages - 3) {
                    pages.push(1);
                    pages.push('...');
                    for (let i = this.totalPages - 4; i <= this.totalPages; i++) {
                        pages.push(i);
                    }
                } else {
                    pages.push(1);
                    pages.push('...');
                    for (let i = this.currentPage - 1; i <= this.currentPage + 1; i++) {
                        pages.push(i);
                    }
                    pages.push('...');
                    pages.push(this.totalPages);
                }
            }

            return pages;
        }
    },
    mounted() {
        this.fetchAlarms();
    },
    methods: {
        async fetchAlarms() {
            this.loading = true;
            try {
                const params = new URLSearchParams({
                    search: this.meterSerial,
                    showActive: 'true',
                    showSnoozed: 'true',
                    showResolved: 'true',
                    perPage: '100',
                    sortBy: 'reading',
                    sortDesc: 'true'
                });

                const response = await axios.get(
                    this.company.buildCompanyApiInternalRoute('alarms/'),
                    { params: Object.fromEntries(params) }
                );

                // Filter to this meter's alarms by ID to avoid false matches
                // from the search hitting meters whose name contains this serial number.
                const data = response.data.data || [];
                this.alarms = data.filter(alarm => alarm.device_id === this.meterId);
                // The API caps results at 100 across all matched meters, so if we
                // hit that cap, this meter's alarms may be incomplete.
                this.truncated = data.length >= 100;
            } catch (error) {
                console.error('Failed to fetch alarms:', error);
                this.alarms = [];
            } finally {
                this.loading = false;
            }
        },
        navigateToAlarm(alarmId) {
            window.location.href = this.company.buildCompanyRoute(`alarms/${alarmId}`);
        },
        commentCount(alarm) {
            const count = alarm.comments_count || 0;
            if (count === 0) {
                return '0 Comments';
            } else if (count === 1) {
                return '1 Comment';
            } else {
                return `${count} Comments`;
            }
        },
        goToPage(page) {
            if (page === '...' || page < 1 || page > this.totalPages) {
                return;
            }
            this.currentPage = page;
        },
        nextPage() {
            if (this.currentPage < this.totalPages) {
                this.currentPage++;
            }
        },
        previousPage() {
            if (this.currentPage > 1) {
                this.currentPage--;
            }
        },
        sortTable(column) {
            if (this.sortBy === column) {
                if (this.sortDesc) {
                    this.sortDesc = false;
                } else {
                    this.sortBy = null;
                    this.sortDesc = true;
                }
            } else {
                this.sortBy = column;
                this.sortDesc = true;
            }
            this.currentPage = 1;
        }
    },
    watch: {
        statusFilter() {
            this.currentPage = 1;
        },
        typeFilter() {
            this.currentPage = 1;
        }
    }
};
</script>

<style>
/* Ensure consistent placeholder styling for both Select2 dropdowns */
.select2-container--default .select2-selection--single .select2-selection__placeholder,
.select2-container--default .select2-selection--multiple .select2-selection__placeholder {
    color: #9ca3af !important; /* text-gray-400 */
}

.select2-container--default .select2-selection--single .select2-selection__rendered,
.select2-container--default .select2-selection--multiple .select2-selection__rendered {
    color: #374151; /* text-gray-700 - for selected values */
}
</style>
